import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Nav from "@/components/Nav";
import DropZone from "@/components/sidebyside/DropZone";
import ImagePanel from "@/components/sidebyside/ImagePanel";
import ChatPanel from "@/components/sidebyside/ChatPanel";
import CalcPanel from "@/components/sidebyside/CalcPanel";
import { track } from "@/lib/analytics";

const TOOLS = [
  { id: "chat",  label: "AI Chat" },
  { id: "sai",   label: "SAI Estimator" },
  { id: "loans", label: "Loan Calculator" },
  { id: "decoder", label: "Aid Letter Decoder" },
];

/**
 * SideBySidePage, split layout where one side is a AidWaive tool and the
 * other side is an image the user uploaded. Image stays in browser memory
 * via URL.createObjectURL, never leaves the device.
 */
export default function SideBySidePage() {
  const [tool, setTool] = useState("chat");
  const [file, setFile] = useState(null); // { url, name, mime }

  useEffect(() => { track.tool_opened("sidebyside"); }, []);

  // Revoke object URL on unmount to free memory
  useEffect(() => {
    return () => { if (file?.url) URL.revokeObjectURL(file.url); };
  }, [file?.url]);

  const handleFile = (f) => {
    if (file?.url) URL.revokeObjectURL(file.url);
    const url = URL.createObjectURL(f);
    setFile({ url, name: f.name, mime: f.type });
  };

  const clearFile = () => {
    if (file?.url) URL.revokeObjectURL(file.url);
    setFile(null);
  };

  return (
    <div className="theme-light min-h-screen flex flex-col bg-white">
      <Nav />

      <main className="flex-1 bg-white">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          {/* Header */}
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm mb-7 text-gray-500 hover:text-gray-900 transition-colors"
            data-testid="sbs-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to AidWaive
          </Link>

          <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">
                Bonus tool · Side-by-Side
              </div>
              <h1 className="font-heading leading-[1.05] mb-4 text-gray-900"
                style={{ fontSize: "clamp(2.2rem, 4vw, 3.2rem)", letterSpacing: "-0.012em" }}>
                Keep your documents visible while you <em style={{ fontStyle: "italic", color: "#1d4ed8" }}>work.</em>
              </h1>
              <p className="text-base sm:text-lg max-w-2xl text-gray-500 leading-relaxed">
                Upload your tax return or aid letter on one side and use any AidWaive tool on the other.
                Nothing you upload leaves your browser.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium tracking-[0.16em] uppercase"
              style={{ background: "#ecfdf5", color: "#15803d", border: "1px solid #a7f3d0" }}
              data-testid="sbs-privacy-pill">
              <ShieldCheck className="w-3 h-3" /> Stays on your device
            </span>
          </div>

          {/* Tool selector */}
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <label htmlFor="sbs-tool-select" className="text-xs uppercase tracking-[0.18em]" style={{ color: "#9ca3af" }}>
              Left panel
            </label>
            <select
              id="sbs-tool-select"
              className="select-clean"
              style={{ width: "auto", minWidth: 180 }}
              value={tool}
              onChange={(e) => setTool(e.target.value)}
              data-testid="sbs-tool-select">
              {TOOLS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          {/* Split layout. Mobile: image on top, tool below. Desktop: 50/50 side-by-side. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ minHeight: "70vh" }}>
            {/* RIGHT (mobile-top): document */}
            <div className="order-1 md:order-2 h-[280px] md:h-auto" data-testid="sbs-doc-side">
              {file ? (
                <ImagePanel url={file.url} fileName={file.name} mime={file.mime} onClear={clearFile} />
              ) : (
                <DropZone onFile={handleFile} />
              )}
            </div>

            {/* LEFT (mobile-bottom): tool */}
            <div className="order-2 md:order-1 rounded-xl overflow-hidden"
              style={{ background: "#ffffff", border: "1px solid #e5e7eb", minHeight: 480 }}
              data-testid="sbs-tool-side">
              {tool === "chat"    && <ChatPanel />}
              {tool === "sai"     && <CalcPanel initialTab="sai" />}
              {tool === "loans"   && <CalcPanel initialTab="loans" />}
              {tool === "decoder" && <CalcPanel initialTab="decoder" />}
            </div>
          </div>

          <p className="text-[11px] mt-6 max-w-xl" style={{ color: "#9ca3af" }} data-testid="sbs-privacy-note">
            Files are loaded into your browser using a temporary local URL and discarded the moment you click "Clear" or close the tab.
            AidWaive never sees them.
          </p>
        </div>
      </main>

      {/* Slim light footer to match other tool pages */}
      <footer className="border-t mt-12" style={{ borderColor: "#e5e7eb", background: "#ffffff" }}>
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 text-xs flex justify-between"
          style={{ color: "#9ca3af" }}
          data-testid="sbs-footer">
          <Link to="/" className="hover:underline">← All AidWaive tools</Link>
          <div className="flex gap-5">
            <Link to="/journey" className="hover:underline">The Journey</Link>
            <Link to="/legal/privacy" className="hover:underline">Privacy</Link>
            <Link to="/legal/terms" className="hover:underline">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
